/**
 * デモモード用の固定データ（フィクスチャ）
 *
 * このファイルが「実データの差し込み口」。デモに出す動画・ユーザー・コメントは
 * すべてここを書き換えるだけで反映される（demoAdapter / getVideoStreamUrl が参照）。
 *
 * 注意:
 * - DEMO_MODE=true のときのみ読み込まれる。本番挙動には一切影響しない。
 * - 許可リストのパスワードはビルド成果物に含まれる（デモデータのみのため許容）。
 *   本当に第三者を締め出したい場合は Cloudflare Access 等を手前に被せること。
 */

import type { LoginRes, UserProfile } from '../services/authApi/authApi.types';
import type { Video, Specialty } from '../services/videoApi/videoApi.types';
import type { Comment } from '../services/commentApi/commentApi.types';
import type { User } from '../services/userApi/userApi.types';
import type { LiveStream } from '../services/liveStreamApi/liveStreamApi';

// ---- ログイン許可リスト（指定した人だけログインできる） ----
export interface DemoAccount {
  email: string;
  password: string;
  /**
   * 有効期限（ISO文字列）。過ぎるとログインを拒否する。省略時は無期限。
   *
   * 社外に渡す一時アカウント用。デモは静的サイトでサーバ側の失効機構が無いため、
   * 「渡したきり消し忘れる」を防ぐ手段がこれしかない。期限切れの account を
   * 消すのではなく残しておくと、いつ誰に何を渡したかが履歴に残る。
   */
  expiresAt?: string;
  /** ログインレスポンス用のユーザー（/auth/login が返す形） */
  user: LoginRes['user'];
  /** /auth/me・プロファイル用の詳細ユーザー */
  profile: UserProfile;
}

/**
 * 社外へ渡すアカウントを作るときの注意（重要）
 *
 * 1. **uploader と同じ id を使わないこと。** 全動画の投稿者は id 1001（デモ太郎）で、
 *    KebabMenu は `isOwner` でダウンロード・編集・削除を出し分けている。
 *    1001 以外の id にすれば、これらのボタンはUIに出ない。
 * 2. **role は 'user' にすること。** 'admin' だとユーザー管理・管理者通知が見える。
 * 3. **ただしダウンロードを技術的に防げてはいない。** 動画は公開S3の絶対URLで
 *    配信しており、再生できる人は開発者ツールやブラウザの「動画を保存」で
 *    実ファイルを取得できる。ボタンを隠すのは導線を消すだけで、防御ではない。
 *    本気で止めるなら CloudFront + 署名付きURL 等への移行が要る。
 *    現状の抑止はウォーターマーク（視聴者の氏名・メール・時刻を常時表示）による
 *    トレーサビリティ。渡す相手ごとに別アカウントにしておくと、流出時に
 *    どのアカウントから出たかが映像から分かる。
 */

const now = '2026-06-01T00:00:00.000Z';

/** N 日前の ISO 文字列。デモの日付が時間経過で腐らないようにするため。 */
const daysAgo = (n: number): string =>
  new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString();

export const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    email: 'demo@surgilink.jp',
    password: 'medtube-demo',
    user: { id: '1001', name: 'デモ太郎', role: 'admin', email: 'demo@surgilink.jp', icon_path: null },
    profile: {
      id: '1001',
      email: 'demo@surgilink.jp',
      name: 'デモ太郎',
      role: 'admin',
      hospital_id: '1',
      hospital_name: 'SurgiLinkデモ病院',
      icon_path: null,
      created_at: now,
      updated_at: now,
    },
  },
  {
    email: 'guest@surgilink.jp',
    password: 'medtube-guest',
    user: { id: '1002', name: 'ゲスト医師', role: 'user', email: 'guest@surgilink.jp', icon_path: null },
    profile: {
      id: '1002',
      email: 'guest@surgilink.jp',
      name: 'ゲスト医師',
      role: 'user',
      hospital_id: '1',
      hospital_name: 'SurgiLinkデモ病院',
      icon_path: null,
      created_at: now,
      updated_at: now,
    },
  },
  // ---- 社外配布用の一時アカウント ----
  //
  // **渡す相手ごとに1つ作る。** 汎用の共有アカウントは置かない。
  // name とメールはウォーターマークに出るため、相手が特定できる値にしておくことで
  // 流出時にどのアカウントから出たかが映像から分かる。共有アカウントにすると
  // この追跡ができなくなり、分ける意味が消える。
  //
  // 使い終わったら消さずに expiresAt を過ぎたまま残す（いつ誰に渡したかの履歴になる）。
  {
    // 東大和病院（2026-07-30 発行）
    email: 'higashiyamato@surgilink.jp',
    password: 'medtube-hy2026',
    // JST 2026-09-01 00:00 で失効
    expiresAt: '2026-08-31T15:00:00.000Z',
    user: {
      id: '1006',
      name: '東大和病院 見学',
      role: 'user',
      email: 'higashiyamato@surgilink.jp',
      icon_path: null,
    },
    profile: {
      id: '1006',
      email: 'higashiyamato@surgilink.jp',
      name: '東大和病院 見学',
      role: 'user',
      hospital_id: '1',
      hospital_name: 'SurgiLinkデモ病院',
      icon_path: null,
      created_at: now,
      updated_at: now,
    },
  },
  {
    // 問い合わせ経由（2026-09-06 発行）／荻窪病院か名張市立病院か所属未確定
    // メールのローカル部は乱数。バンドルは誰でも取得できるため、
    // 先方の実アドレスは置かない（curl でそのまま読める）。
    email: 'demo-ube6mx@surgilink.jp',
    password: 'dvdv-q8bn-3aeh',
    // JST 2026-10-01 00:00 で失効
    expiresAt: '2026-09-30T15:00:00.000Z',
    user: {
      id: '1007',
      name: '高橋先生 見学',
      role: 'user',
      email: 'demo-ube6mx@surgilink.jp',
      icon_path: null,
    },
    profile: {
      id: '1007',
      email: 'demo-ube6mx@surgilink.jp',
      name: '高橋先生 見学',
      role: 'user',
      hospital_id: '1',
      hospital_name: 'SurgiLinkデモ病院',
      icon_path: null,
      created_at: now,
      updated_at: now,
    },
  },
  {
    // 問い合わせ経由（2026-09-06 発行）。中国・医学院の主治医師（attending）
    email: 'demo-3isnir@surgilink.jp',
    password: 'vq7y-pyja-zebc',
    // JST 2026-10-01 00:00 で失効
    expiresAt: '2026-09-30T15:00:00.000Z',
    user: {
      id: '1008',
      name: 'Zheng Kun 先生 見学',
      role: 'user',
      email: 'demo-3isnir@surgilink.jp',
      icon_path: null,
    },
    profile: {
      id: '1008',
      email: 'demo-3isnir@surgilink.jp',
      name: 'Zheng Kun 先生 見学',
      role: 'user',
      hospital_id: '1',
      hospital_name: 'SurgiLinkデモ病院',
      icon_path: null,
      created_at: now,
      updated_at: now,
    },
  },
];

/** デモ用の固定トークン（実体は使われない。SessionManager 保存用） */
export const DEMO_TOKEN = 'demo-token';

// ---- 診療科 ----
export const DEMO_SPECIALTIES: Specialty[] = [
  { id: 1, name: '整形外科' },
  { id: 2, name: '呼吸器外科' },
  { id: 3, name: '脳神経外科' },
];

const demoUploader = {
  id: '1001',
  name: 'デモ太郎',
  icon_path: undefined as string | undefined,
  hospital: { id: '1', name: 'SurgiLinkデモ病院' },
};

// ---- デモ用アセットの配信元 ----
//
// 動画とサムネイルはリポジトリに入れず S3 に置く。git は一度コミットした
// バイナリを履歴から落とせず、手術映像を1回でも入れると以後どのクローンにも
// 永久に残るため（詳細は .gitignore と docs/demo-mode.md）。
//
// この定数はデモモード（DEMO_MODE=true）でのみ読み込まれ、本番の動画配信経路には
// 一切関与しない。バケットを移すときはここ1行を変えれば全エントリに反映される。
export const DEMO_ASSET_BASE = 'https://medtube-demo-assets.s3.ap-northeast-1.amazonaws.com';

// ---- 動画 ----
// path / thumbnail は DEMO_ASSET_BASE 配下の絶対URL。
// 差し替えるときは S3 にファイルを置き、ここのファイル名を更新する。
// リポジトリに mp4 を置いても .gitignore で弾かれる。
//
// ⚠️ 2026-07-29 以降、ここに並んでいるのは**実際の手術映像**（提供: SurgiLink）。
//    合成サンプルではないので、タイトル・説明文に「実際の手術映像ではありません」と
//    書かないこと。デモサイトは実質公開（許可リストのパスワードはバンドルに含まれる）
//    のため、動画を追加するときは公開してよい素材かを必ず確認する。
//
// specialty_attributes の 部位 / AO分類 の値は **`src/data/orthopedicsData.json` の語彙に
// 必ず一致させること**。値は自由入力なので表示は壊れないが、部位がマスタと1文字でもズレると
// 編集画面で AO分類 の候補がその部位向けに絞られず全件になる（useVideoEditSpecialtySection が
// マスタとの完全一致で候補を引くため）。マスタは「部位ラベル + 亜型まで含む番号」で、
// 例えば足関節は `腓骨遠位部` / `44-B1`、中足骨は `中足骨,趾骨骨幹部` / `82-A1`。
//
// ⚠️ TODO(監修): 番号の**亜型（末尾の1桁）**は骨折像を読まないと確定できないため
//    推定値。部位と分類の大分類（31/44/41/82/11）は診断名から一意に決まるので確か。
//    整形外科医の確認を受けて亜型を確定させること。
//
// 日付は「N日前」表示に使われるため相対生成する。固定値にすると時間の経過で
// 「1年前の動画」と「30分前に開始した配信中」が並ぶ、更新の止まったサービスに見える。
export const DEMO_VIDEOS: Video[] = [
  {
    id: 1,
    title: '大腿骨頸部骨折 人工骨頭挿入術（BHA）',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-hip-bha.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-hip-bha.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-hip-bha.mp4`,
    view_count: 1280,
    views: 1280,
    duration: 101,
    publish_date: daysAgo(9),
    created_at: daysAgo(9),
    surgery_date: daysAgo(12),
    visibility: 'public',
    tags: '整形外科,大腿骨,人工骨頭,BHA',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '大腿骨頸部骨折に対する人工骨頭挿入術（BHA）の術野です。展開から設置までを収録しています。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '大腿骨近位部' },
      { attribute_key: 'AO分類', attribute_value: '31-B3' },
    ],
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 2,
    title: '脛骨高原骨折 プレート固定術',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-tibial-plateau-orif.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-tibial-plateau-orif.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-tibial-plateau-orif.mp4`,
    view_count: 514,
    views: 514,
    duration: 47,
    publish_date: daysAgo(14),
    created_at: daysAgo(14),
    surgery_date: daysAgo(17),
    visibility: 'public',
    tags: '整形外科,脛骨,脛骨高原骨折,プレート固定',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '脛骨高原骨折に対するプレート固定術の術野です。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '下腿骨近位部' },
      { attribute_key: 'AO分類', attribute_value: '41-B1' },
    ],
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 3,
    title: '中足骨骨折 プレート固定術',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-metatarsal-orif.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-metatarsal-orif.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-metatarsal-orif.mp4`,
    view_count: 366,
    views: 366,
    duration: 98,
    publish_date: daysAgo(17),
    created_at: daysAgo(17),
    surgery_date: daysAgo(20),
    visibility: 'public',
    tags: '整形外科,中足骨,足部,プレート固定',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '中足骨骨折に対するプレート固定術の術野です。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '中足骨,趾骨骨幹部' },
      { attribute_key: 'AO分類', attribute_value: '82-A1' },
    ],
    is_liked: false,
    is_favorited: false,
  },
  // ---- 2026-09-17 追加の7本（id 4〜10）----
  // ⚠️ TODO(監修): タイトル・説明・部位・AO分類は映像とファイル名からの叩き台。
  //    相原先生の確認を受けて確定させること。TKA / THA は骨折ではないため部位・AO分類を付けない。
  {
    id: 4,
    title: '人工膝関節全置換術（TKA）',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-knee-tka.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-knee-tka.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-knee-tka.mp4`,
    view_count: 842,
    views: 842,
    duration: 201,
    publish_date: daysAgo(5),
    created_at: daysAgo(5),
    surgery_date: daysAgo(8),
    visibility: 'public',
    tags: '整形外科,膝関節,人工関節,TKA',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '変形性膝関節症に対する人工膝関節全置換術（TKA）の術野です。展開から骨切りまでを収録しています。',
    specialty: { id: 1, name: '整形外科' },
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 5,
    title: '仰臥位前外側アプローチ（ALS）による人工股関節全置換術（THA）',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-hip-tha-als.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-hip-tha-als.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-hip-tha-als.mp4`,
    view_count: 693,
    views: 693,
    duration: 170,
    publish_date: daysAgo(7),
    created_at: daysAgo(7),
    surgery_date: daysAgo(10),
    visibility: 'public',
    tags: '整形外科,股関節,人工関節,THA,ALS',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '仰臥位前外側アプローチ（ALS）で行う人工股関節全置換術（THA）の術野です。',
    specialty: { id: 1, name: '整形外科' },
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 6,
    title: '膝蓋骨骨折 DTJスクリュー固定術',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-patella-dtj.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-patella-dtj.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-patella-dtj.mp4`,
    view_count: 421,
    views: 421,
    duration: 151,
    publish_date: daysAgo(11),
    created_at: daysAgo(11),
    surgery_date: daysAgo(14),
    visibility: 'public',
    tags: '整形外科,膝蓋骨,膝蓋骨骨折,DTJ',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '膝蓋骨骨折に対するDTJスクリューを用いた骨接合術の術野です。透視での確認を含みます。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '膝蓋骨' },
      { attribute_key: 'AO分類', attribute_value: '45-C1' },
    ],
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 7,
    title: '上腕骨通顆骨折 骨接合術',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-distal-humerus-transcondylar.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-distal-humerus-transcondylar.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-distal-humerus-transcondylar.mp4`,
    view_count: 377,
    views: 377,
    duration: 191,
    publish_date: daysAgo(4),
    created_at: daysAgo(4),
    surgery_date: daysAgo(7),
    visibility: 'public',
    tags: '整形外科,上腕骨,通顆骨折,骨接合術',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '上腕骨通顆骨折に対する骨接合術の術野です。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '上腕骨遠位部' },
      { attribute_key: 'AO分類', attribute_value: '13-A2' },
    ],
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 8,
    title: '大腿骨頸部骨折 骨接合術',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-femoral-neck-osteosynthesis.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-femoral-neck-osteosynthesis.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-femoral-neck-osteosynthesis.mp4`,
    view_count: 298,
    views: 298,
    duration: 132,
    publish_date: daysAgo(3),
    created_at: daysAgo(3),
    surgery_date: daysAgo(6),
    visibility: 'public',
    tags: '整形外科,大腿骨,大腿骨頸部骨折,骨接合術',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '大腿骨頸部骨折に対する骨接合術です。透視画面でのピン刺入位置の確認を収録しています。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '大腿骨近位部' },
      { attribute_key: 'AO分類', attribute_value: '31-B1' },
    ],
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 9,
    title: '大腿骨顆部骨折 観血的整復固定術',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-distal-femur-orif.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-distal-femur-orif.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-distal-femur-orif.mp4`,
    view_count: 256,
    views: 256,
    duration: 186,
    publish_date: daysAgo(2),
    created_at: daysAgo(2),
    surgery_date: daysAgo(5),
    visibility: 'public',
    tags: '整形外科,大腿骨,大腿骨顆部骨折,観血的整復固定術',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '大腿骨顆部骨折に対する観血的整復固定術の術野です。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '大腿骨遠位部' },
      { attribute_key: 'AO分類', attribute_value: '33-C1' },
    ],
    is_liked: false,
    is_favorited: false,
  },
  {
    id: 10,
    title: '橈骨頚部骨折 観血的整復固定術',
    thumbnail_url: `${DEMO_ASSET_BASE}/demo-radial-neck-orif.jpg`,
    thumbnail: `${DEMO_ASSET_BASE}/demo-radial-neck-orif.jpg`,
    path: `${DEMO_ASSET_BASE}/demo-radial-neck-orif.mp4`,
    view_count: 334,
    views: 334,
    duration: 171,
    publish_date: daysAgo(6),
    created_at: daysAgo(6),
    surgery_date: daysAgo(9),
    visibility: 'public',
    tags: '整形外科,橈骨,橈骨頚部骨折,観血的整復固定術',
    uploader: demoUploader,
    uploader_id: '1001',
    description: '橈骨頚部骨折に対する観血的整復固定術の術野です。',
    specialty: { id: 1, name: '整形外科' },
    specialty_attributes: [
      { attribute_key: '部位', attribute_value: '前腕骨近位部' },
      { attribute_key: 'AO分類', attribute_value: '21-A2' },
    ],
    is_liked: false,
    is_favorited: false,
  },
];

// ---- コメント（video_id ごと） ----
// created_at は各動画の publish_date より後にしておくこと。前だと
// 「投稿前に付いたコメント」になって一覧で不自然に見える。
export const DEMO_COMMENTS: Comment[] = [
  {
    id: 1,
    video_id: 1,
    user_id: '1002',
    content: '展開の手順がとても参考になりました。',
    created_at: daysAgo(8),
    user: { id: '1002', name: 'ゲスト医師', icon_path: null },
    childCount: 0,
    likeCount: 3,
  },
  {
    id: 2,
    video_id: 2,
    user_id: '1003',
    content: '関節面の整復とプレート設置の順序が参考になりました。',
    created_at: daysAgo(12),
    user: { id: '1003', name: '佐藤 整形', icon_path: null },
    childCount: 0,
    likeCount: 1,
  },
  {
    id: 3,
    video_id: 3,
    user_id: '1002',
    content: 'プレートの設置位置について後で相談させてください。',
    created_at: daysAgo(13),
    user: { id: '1002', name: 'ゲスト医師', icon_path: null },
    childCount: 0,
    likeCount: 2,
  },
  {
    id: 4,
    video_id: 2,
    user_id: '1003',
    content: '透視での確認の入れ方、勉強になります。',
    created_at: daysAgo(13),
    user: { id: '1003', name: '佐藤 整形', icon_path: null },
    childCount: 0,
    likeCount: 4,
  },
];

// ---- 病院 ----
export const DEMO_HOSPITAL = { id: '1', name: 'SurgiLinkデモ病院' };

// ---- ユーザー（管理画面用のデモ名簿） ----
export const DEMO_USERS: User[] = [
  { id: '1001', email: 'demo@surgilink.jp', name: 'デモ太郎', role: 'admin', hospital_id: '1', hospital_name: 'SurgiLinkデモ病院', created_at: now, updated_at: now },
  { id: '1002', email: 'guest@surgilink.jp', name: 'ゲスト医師', role: 'user', hospital_id: '1', hospital_name: 'SurgiLinkデモ病院', created_at: now, updated_at: now },
  { id: '1003', email: 'sato@surgilink.jp', name: '佐藤 整形', role: 'user', hospital_id: '1', hospital_name: 'SurgiLinkデモ病院', created_at: now, updated_at: now },
  { id: '1004', email: 'suzuki@surgilink.jp', name: '鈴木 呼吸器', role: 'user', hospital_id: '1', hospital_name: 'SurgiLinkデモ病院', created_at: now, updated_at: now },
  { id: '1005', email: 'takahashi@surgilink.jp', name: '高橋 脳外', role: 'admin', hospital_id: '1', hospital_name: 'SurgiLinkデモ病院', created_at: now, updated_at: now },
];

// ---- いいねした人（likers モーダル用） ----
export const DEMO_LIKERS: { id: string; name: string; icon_path: string | null }[] = [
  { id: '1002', name: 'ゲスト医師', icon_path: null },
  { id: '1003', name: '佐藤 整形', icon_path: null },
  { id: '1004', name: '鈴木 呼吸器', icon_path: null },
];

// ---- ライブ配信（配信中／予定／アーカイブの3状態を用意） ----
// live の視聴は hls-session が録画済みの動画を返す（demoAdapter 参照）。
// タイトルは DEMO_VIDEOS と揃えてある: ls-1 は動画1（配信中の想定）、
// ls-3 は archive_video_id: '3' で動画3に着地する。ここがズレると
// 「肺葉切除のアーカイブを開いたら足関節が再生される」事故になる。
export const DEMO_LIVE_STREAMS: LiveStream[] = [
  {
    id: 'ls-1',
    hospital_id: '1',
    created_by: '1001',
    specialty_id: null,
    title: 'ライブ手術：大腿骨頸部骨折 人工骨頭挿入術（BHA）',
    description: 'デモ環境の配信サンプルです。配信中の状態をご確認いただけます。',
    visibility: 'public',
    status: 'live',
    thumbnail_path: `${DEMO_ASSET_BASE}/demo-hip-bha.jpg`,
    // 一覧は LIVE バッジの隣に started_at を「絶対日時」で出すため、固定値にすると
    // 「半年前に開始した配信中」になって腐る。常に「30分前に開始」に見せる。
    started_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    ended_at: null,
    last_heartbeat_at: new Date().toISOString(),
    archive_video_id: null,
    archive_video_accessible: false,
    created_at: new Date(Date.now() - 35 * 60 * 1000).toISOString(),
    updated_at: new Date().toISOString(),
    hls_url: '',
  },
  {
    id: 'ls-2',
    hospital_id: '1',
    created_by: '1005',
    specialty_id: null,
    title: '配信予定：人工膝関節置換術（TKA）',
    description: '開始までお待ちください。デモ環境の配信予定サンプルです。',
    visibility: 'public',
    status: 'scheduled',
    thumbnail_path: `${DEMO_ASSET_BASE}/demo-tibial-plateau-orif.jpg`,
    started_at: null,
    ended_at: null,
    last_heartbeat_at: null,
    archive_video_id: null,
    archive_video_accessible: false,
    created_at: daysAgo(2),
    updated_at: daysAgo(2),
    hls_url: '',
  },
  {
    id: 'ls-3',
    hospital_id: '1',
    created_by: '1001',
    specialty_id: null,
    title: 'アーカイブ：中足骨骨折 プレート固定術',
    description: '配信は終了しました。録画アーカイブをご覧いただけます。',
    visibility: 'public',
    status: 'ended',
    thumbnail_path: `${DEMO_ASSET_BASE}/demo-metatarsal-orif.jpg`,
    // アーカイブの着地先は動画3。配信日が動画の公開日より後だと
    // 「配信より前に録画が公開されている」ことになるため、17日前に揃える。
    started_at: daysAgo(17),
    ended_at: daysAgo(17),
    last_heartbeat_at: daysAgo(17),
    archive_video_id: '3',
    archive_video_accessible: true,
    created_at: daysAgo(17),
    updated_at: daysAgo(17),
    hls_url: '',
  },
];

/**
 * デモ用ストリーミングURLを返す。
 * 該当動画の path（S3 の絶対URL）をそのまま返す。
 */
export const getDemoStreamUrl = (videoId: string | number): string => {
  const idStr = String(videoId);
  const video = DEMO_VIDEOS.find(v => String(v.id) === idStr);
  if (video?.path) {
    // 絶対URL（Cloudflare R2 等）はそのまま、相対パスは public 配下として解決する
    return /^https?:\/\//.test(video.path) ? video.path : `/${video.path}`;
  }
  // 未知IDのフォールバック。黙って別動画を流すと取り違えに気づけないため警告する。
  console.warn(`[demo] 未知の videoId のためサンプル動画にフォールバックします: ${idStr}`);
  return `${DEMO_ASSET_BASE}/demo-hip-bha.mp4`;
};
